/**
 * Celebration — glow-puff travel layer.
 *
 * On every word-found event we render a single warm glow that drifts from
 * the found word's grid centroid to its entry in the "found list" sidebar.
 *
 * Strict policy: no hand-drawn SVG depictive imagery. The glow is a radial-
 * gradient circle (a structural geometric primitive), not a glyph. The
 * theme palette (var(--th-glow), var(--th-accent)) tints it.
 *
 * Travel coords are read from the actual DOM rects of the celebrated cells
 * and the matching `<li>` in the found list, so the motion lands precisely
 * on the destination tag — not on a guessed position.
 */
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useEffect, useState, type RefObject } from 'react';

import { type Cell } from '@/games/word-search/engine';

import type { ThemeSurface } from './themes';

import './Celebration.css';

interface BurstSpec {
  id: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  word: string;
}

export interface CelebrationProps {
  surface: ThemeSurface;
  event: { word: string; cells: readonly Cell[] } | null;
  gridRef: RefObject<HTMLDivElement | null>;
  foundListRef: RefObject<HTMLUListElement | null>;
}

const TRAVEL_DURATION_MS = 1000;

export function Celebration({
  event,
  gridRef,
  foundListRef,
}: CelebrationProps): React.JSX.Element {
  const reduced = useReducedMotion();
  const [bursts, setBursts] = useState<BurstSpec[]>([]);
  const [nextId, setNextId] = useState(0);
  const [lastWord, setLastWord] = useState<string | null>(null);

  useEffect(() => {
    if (!event) return;
    if (event.word === lastWord) return;
    setLastWord(event.word);

    const grid = gridRef.current;
    const list = foundListRef.current;
    if (!grid) return;

    const cells = event.cells;
    if (cells.length === 0) return;
    const allCells = Array.from(grid.querySelectorAll('.ts-cell'));
    const cols = Math.round(Math.sqrt(allCells.length));
    let sx = 0;
    let sy = 0;
    let count = 0;
    for (const c of cells) {
      const idx = c[0] * cols + c[1];
      const el = allCells[idx] as HTMLElement | undefined;
      if (!el) continue;
      const r = el.getBoundingClientRect();
      sx += r.left + r.width / 2;
      sy += r.top + r.height / 2;
      count += 1;
    }
    if (count === 0) return;
    const fromX = sx / count;
    const fromY = sy / count;

    let toX = fromX + 200;
    let toY = fromY;
    if (list) {
      const items = list.querySelectorAll('li');
      for (const li of Array.from(items)) {
        const txt = li.querySelector('.ts-play__words-text')?.textContent;
        if (txt === event.word) {
          const r = li.getBoundingClientRect();
          toX = r.left + r.width / 2;
          toY = r.top + r.height / 2;
          break;
        }
      }
    }

    const id = nextId;
    setBursts((prev) => [...prev, { id, fromX, fromY, toX, toY, word: event.word }]);
    setNextId((n) => n + 1);
    const t = window.setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== id));
    }, TRAVEL_DURATION_MS + 200);
    return () => window.clearTimeout(t);
  }, [event, gridRef, foundListRef, lastWord, nextId]);

  if (reduced) {
    return <div className="ts-celebration ts-celebration--reduced" aria-hidden="true" />;
  }

  return (
    <div className="ts-celebration" aria-hidden="true">
      <AnimatePresence>
        {bursts.map((b) => (
          <BurstView key={b.id} burst={b} />
        ))}
      </AnimatePresence>
    </div>
  );
}

/**
 * Glow puff that arcs from the grid centroid to the found-list entry. The
 * arc rises before settling so the motion reads as "the word lifts and
 * lands in the list" rather than a straight slide.
 */
function BurstView({ burst }: { burst: BurstSpec }): React.JSX.Element {
  const cx = (burst.fromX + burst.toX) / 2;
  const cy = Math.min(burst.fromY, burst.toY) - 60 - Math.random() * 20;
  return (
    <>
      <motion.span
        className="ts-celebration__glow ts-celebration__glow--travel"
        initial={{ x: burst.fromX, y: burst.fromY, opacity: 0, scale: 0.5 }}
        animate={{
          x: [burst.fromX, cx, burst.toX],
          y: [burst.fromY, cy, burst.toY],
          opacity: [0, 1, 1, 0],
          scale: [0.5, 1.0, 0.9, 0.6],
        }}
        transition={{
          duration: TRAVEL_DURATION_MS / 1000,
          times: [0, 0.2, 0.85, 1],
          ease: [0.25, 1, 0.5, 1],
        }}
        style={{ position: 'fixed', left: 0, top: 0, pointerEvents: 'none' }}
      />
      {/* Source pop — a brief swelling glow at the grid centroid. */}
      <motion.span
        className="ts-celebration__glow ts-celebration__glow--source"
        initial={{ x: burst.fromX, y: burst.fromY, opacity: 0, scale: 0.4 }}
        animate={{ opacity: [0, 0.85, 0], scale: [0.4, 1.6, 2.0] }}
        transition={{ duration: 0.55, times: [0, 0.4, 1], ease: [0.25, 1, 0.5, 1] }}
        style={{ position: 'fixed', left: 0, top: 0, pointerEvents: 'none' }}
      />
    </>
  );
}
